import {motion} from "framer-motion";
import {memo, useEffect, useMemo, useState} from "react";
import {useEmbarqueDependencias} from "../../../../../../contexts/EmbarqueDependenciasContext.tsx";
import {useFiltro} from "../../../../../../contexts/FiltroContext.tsx";
import {useProfile} from "../../../../../../contexts/ProfileContext.tsx";
import {SelectInputOption} from "../../../../../../core/data-entry/SelectInputField.tsx";
import {
    combinedFiltroEmbarque,
    FiltroEmbarqueCompletoEnum,
} from "../../../../../../models/FiltroEmbarqueCompleto.ts";
import InputTags from "../../data-entry/InputTags.tsx";
import SelectTags from "../../data-entry/SelectTags.tsx";

function RenderCombinedFiltros() {
    const {
        filial,
        empresaSub,
        pedido,
        status,
        meioPagamento,
        mercadoria,
        motorista,
        proprietario,
        cidade,
        placaCarreta,
        placaCavalo,
        tipoCarroceria,
        tipoVeiculo,
        clientes
    } = useEmbarqueDependencias();
    const {dadosFilial} = useProfile();
    const {filtroEmbarque} = useFiltro();

    const [tagsState, setTagsState] = useState<
        Record<string, SelectInputOption[]>
    >({});

    const allOptionsFiltro = useMemo(() => {
        return {
            [FiltroEmbarqueCompletoEnum.UnidEmbarqueIds]: dadosFilial?.map(
                (item) => ({
                    title: item.nomeFilial,
                    obj: item.id,
                })
            ),
            [FiltroEmbarqueCompletoEnum.EmpresaIds]: empresaSub.map((item) => ({
                title: item.nome,
                obj: item.idEmpresaSub,
            })),
            [FiltroEmbarqueCompletoEnum.FilialIds]: filial.map((item) => ({
                title: item.nome,
                obj: item.idFilial,
            })),
            [FiltroEmbarqueCompletoEnum.MercadoriaIds]: mercadoria.map((item) => ({
                title: item.nome,
                obj: item.idMercadoria,
            })),
            [FiltroEmbarqueCompletoEnum.MotoristaIds]: motorista.map((item) => ({
                title: item.nome,
                obj: item.idMotorista,
            })),
            [FiltroEmbarqueCompletoEnum.MeioPagamentoIds]: meioPagamento.map(
                (item) => ({
                    title: item.nome,
                    obj: item.idMeioPagamento,
                })
            ),
            [FiltroEmbarqueCompletoEnum.StatusIds]: status.map((item) => ({
                title: item.nome,
                obj: item.idStatus,
            })),
            [FiltroEmbarqueCompletoEnum.ProprietarioIds]: proprietario.map(
                (item) => ({
                    title: item.nome,
                    obj: item.idProprietario,
                })
            ),
            [FiltroEmbarqueCompletoEnum.PedidoIds]: pedido
                .sort((a, b) => a.pedido - b.pedido)
                .map((item) => ({
                    title: `${item.pedido} - ${item.tipoRota} @ ${item.rota?.cidadeDestinoRota.cidade} - ${item.rota?.cidadeDestinoRota.abreviacao} - ${item.rota?.cidadeOrigemRota.cidade} - ${item.rota?.cidadeOrigemRota.abreviacao}`,
                    obj: item.idPedido,
                })),
            [FiltroEmbarqueCompletoEnum.PlacaCarretaIds]: placaCarreta
                .map((item) => ({
                    title: item.title,
                    obj: item.obj.carretaId,
                })),
            [FiltroEmbarqueCompletoEnum.PlacaCarreta2Ids]: placaCarreta
                .map((item) => {
                    return {
                        title: item.title,
                        obj: item.obj.carretaId,
                    }
                }),
            [FiltroEmbarqueCompletoEnum.PlacaCavaloIds]: placaCavalo
                .map((item) => ({
                    title: item.title,
                    obj: item.obj.caminhaoId
                })),
            [FiltroEmbarqueCompletoEnum.CidadeDestinoIds]: cidade?.map((item) => ({
                    title: item.cidade,
                    obj: item.idCidadeEstado,
                })) || [],
            [FiltroEmbarqueCompletoEnum.CidadeOrigemIds]: cidade?.map((item) => ({
                    title: item.cidade,
                    obj: item.idCidadeEstado,
                })) || [],
            [FiltroEmbarqueCompletoEnum.TipoCarroceriaIds]: tipoCarroceria.map(
                (item) => ({
                    title: item.nome,
                    obj: item.id,
                })
            ),
            [FiltroEmbarqueCompletoEnum.TipoVeiculoIds]: tipoVeiculo.map((item) => ({
                title: item.nome,
                obj: item.id,
            })),
            [FiltroEmbarqueCompletoEnum.ClienteIds]: clientes.map((item) => ({
                title: `${item.nomeFantasia}`,
                obj: item.idCliente,
            })),
        };
    }, [
        empresaSub,
        dadosFilial,
        filial,
        mercadoria,
        motorista,
        meioPagamento,
        status,
        proprietario,
        pedido,
        tipoCarroceria,
        tipoVeiculo,
        clientes,
    ]);

    useEffect(() => {
        const initialTagsState: Record<string, SelectInputOption[]> = {};
        Object.keys(filtroEmbarque).forEach((key) => {
            const typedKey = key as FiltroEmbarqueCompletoEnum;
            if (Array.isArray(filtroEmbarque[typedKey])) {
                initialTagsState[typedKey] = filtroEmbarque[typedKey].map((id: any) => {
                    const option = allOptionsFiltro[typedKey]?.find((item) => item.obj === id);
                    return option
                        ? {title: option.title, obj: option.obj}
                        : {title: id, obj: id};
                });
            }
        });
        setTagsState(initialTagsState);
    }, [filtroEmbarque, allOptionsFiltro]);

    const handleTagUpdate = (
        key: FiltroEmbarqueCompletoEnum,
        updatedTags: SelectInputOption[]
    ) => {
        setTagsState((prevState) => ({
            ...prevState,
            [key]: updatedTags,
        }));
    };

    const numberFiltroOptions = useMemo(() => {
        return {
            [FiltroEmbarqueCompletoEnum.UnidEmbarqueIds]: dadosFilial
                ?.slice(0, 15)
                .map((item) => ({
                    title: item.nomeFilial,
                    obj: item.id,
                })),
            [FiltroEmbarqueCompletoEnum.EmpresaIds]: empresaSub?.map((item) => ({
                title: item.nome,
                obj: item.idEmpresaSub,
            })),
            [FiltroEmbarqueCompletoEnum.FilialIds]: filial.map((item) => ({
                title: item.nome,
                obj: item.idFilial,
            })),
            [FiltroEmbarqueCompletoEnum.MercadoriaIds]: mercadoria.map((item) => ({
                title: item.nome,
                obj: item.idMercadoria,
            })),
            [FiltroEmbarqueCompletoEnum.MotoristaIds]: motorista
                .slice(0, 15)
                .map((item) => ({
                    title: item.nome,
                    obj: item.idMotorista,
                })),
            [FiltroEmbarqueCompletoEnum.MeioPagamentoIds]: meioPagamento.map(
                (item) => ({
                    title: item.nome,
                    obj: item.idMeioPagamento,
                })
            ),
            [FiltroEmbarqueCompletoEnum.StatusIds]: status.map((item) => ({
                title: item.nome,
                obj: item.idStatus,
            })),
            [FiltroEmbarqueCompletoEnum.ProprietarioIds]: proprietario
                .slice(0, 15)
                .map((item) => ({
                    title: item.nome,
                    obj: item.idProprietario,
                })),
            [FiltroEmbarqueCompletoEnum.PedidoIds]: pedido
                .slice(0, 15)
                .sort((a, b) => a.pedido - b.pedido)
                .map((item) => ({
                    title: `${item.pedido} - ${item.tipoRota} @ ${item.rota?.cidadeDestinoRota.cidade} - ${item.rota?.cidadeDestinoRota.abreviacao} - ${item.rota?.cidadeOrigemRota.cidade} - ${item.rota?.cidadeOrigemRota.abreviacao}`,
                    obj: item.idPedido,
                })),
            [FiltroEmbarqueCompletoEnum.PlacaCarretaIds]: placaCarreta
                .slice(0, 15)
                .map((item) => ({
                    title: item.title,
                    obj: item.obj.carretaId,
                })),
            [FiltroEmbarqueCompletoEnum.PlacaCarreta2Ids]: placaCarreta
                .slice(0, 15)
                .map((item) => {
                    return {
                        title: item.title,
                        obj: item.obj.carretaId,
                    }
                }),
            [FiltroEmbarqueCompletoEnum.PlacaCavaloIds]: placaCavalo
                .slice(0, 15)
                .map((item) => ({
                    title: item.title,
                    obj: item.obj.caminhaoId
                })),
            [FiltroEmbarqueCompletoEnum.CidadeDestinoIds]: cidade?.slice(0, 15)
                .map((item) => ({
                    title: item.cidade,
                    obj: item.idCidadeEstado,
                })) || [],
            [FiltroEmbarqueCompletoEnum.CidadeOrigemIds]: cidade?.slice(0, 15)
                .map((item) => ({
                    title: item.cidade,
                    obj: item.idCidadeEstado,
                })) || [],
            [FiltroEmbarqueCompletoEnum.TipoCarroceriaIds]: tipoCarroceria.map(
                (item) => ({
                    title: item.nome,
                    obj: item.id
                })
            ),
            [FiltroEmbarqueCompletoEnum.TipoVeiculoIds]: tipoVeiculo.map((item) => ({
                title: item.nome,
                obj: item.id,
            })),
            [FiltroEmbarqueCompletoEnum.ClienteIds]: clientes.map((item) => ({
                title: `${item.nomeFantasia}`,
                obj: item.idCliente,
            })),
        } as Partial<Record<FiltroEmbarqueCompletoEnum, { title: string; obj: number }[]>>;
    }, [
        empresaSub,
        dadosFilial,
        filial,
        mercadoria,
        motorista,
        meioPagamento,
        status,
        proprietario,
        pedido,
        placaCarreta,
        placaCavalo,
        tipoCarroceria,
        tipoVeiculo,
        clientes,
        cidade,
    ]);

    return combinedFiltroEmbarque.map(({keySelect, key, title}, index) => (
        <motion.div
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            transition={{duration: 0.2, delay: index * 0.05}}
            key={`${keySelect}-${key}`}
            className="combined-filtro-viagens-container"
        >
            <SelectTags
                key={`${keySelect}-select`}
                title={title}
                filtroKey={keySelect}
                tags={tagsState[keySelect] || []}
                defaultOptions={numberFiltroOptions[keySelect] || []}
                allOptions={allOptionsFiltro[keySelect] || []}
                onTagUpdate={handleTagUpdate}
            />
            <InputTags
                key={`${key}-input`}
                filtroKey={key}
                tags={filtroEmbarque[key] || []}
                showBorder={false}
            />
        </motion.div>
    ));
}

export default memo(RenderCombinedFiltros);
